"use client";
import React, { useState } from "react";
import { MobileHeader } from "../MobileHeader";
import { TabBar } from "../TabBar";

const LeakDetectionDashboard: React.FC = () => {
  const [checkedItems, setCheckedItems] = useState({
    taps: false,
    toilets: false,
    hoses: false,
  });

  const handleCheckboxChange = (item: keyof typeof checkedItems) => {
    setCheckedItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  return (
    <main className="pb-72 mx-auto w-full max-w-[480px]">
      <div className="overflow-hidden w-full bg-white">
        <MobileHeader />
        <div className="pt-8 pb-24 w-full">
          <div className="w-full text-2xl font-semibold leading-none text-gray-900 whitespace-nowrap">
            <div className="px-4 w-full">
              <div className="w-full">
                <div className="w-full">
                  <div className="w-full">Alert</div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 w-full">
            <div className="px-4 w-full">
              <div className="flex relative flex-col items-start p-4 w-full bg-red-100 rounded-xl border border-solid shadow-sm border-[#D92D20]">
                <div className="flex overflow-hidden absolute top-2 right-2 z-0 justify-center items-center p-2 w-9 h-9 rounded-lg min-h-9">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/a44198589ca52d78e53ed7751b4de8acb0c71314?placeholderIfAbsent=true"
                    className="object-contain self-stretch my-auto w-5 aspect-square"
                    alt="Close icon"
                  />
                </div>
                <img
                  src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/0c6f45e1a40c0094fdf0006839b96392f7afb490?placeholderIfAbsent=true"
                  className="object-contain z-0 w-5 rounded-full aspect-square"
                  alt="Leak alert icon"
                />
                <div className="flex z-0 flex-col self-stretch mt-4 w-full text-sm leading-none">
                  <div className="w-full">
                    <div className="font-semibold text-red-500">
                      Potential Leak Detected!
                    </div>
                    <div className="mt-1 text-slate-600">
                      Detected on Jan 15, 2025 • 14:23
                    </div>
                  </div>
                  <div className="flex gap-3 items-start self-start mt-3 font-semibold">
                    <div className="overflow-hidden gap-1.5 self-stretch whitespace-nowrap text-slate-600">
                      Dismiss
                    </div>
                    <div className="overflow-hidden gap-1.5 self-stretch text-blue-700">
                      Learn more
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative px-4 py-5 mt-6 w-full bg-white rounded-xl border border-solid shadow-sm border-[#EAECF0]">
                <div className="z-0 text-base font-semibold text-gray-900">
                  Estimated Water Loss
                </div>
                <div className="flex z-0 gap-4 items-end mt-5 w-full">
                  <div className="flex-1 shrink basis-0">
                    <div className="text-3xl font-semibold leading-none text-red-600">
                      5.2 L/hour
                    </div>
                    <div className="flex gap-2 items-center mt-4 w-full text-sm font-medium leading-none">
                      <div className="flex gap-0.5 justify-center items-center self-stretch my-auto text-center text-red-600 whitespace-nowrap">
                        <img
                          src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/467f740eb07d34ad6b081025eb700bd8166b03ea?placeholderIfAbsent=true"
                          className="object-contain shrink-0 self-stretch my-auto w-5 aspect-square"
                          alt="Water loss indicator"
                        />
                        <div className="self-stretch my-auto">230%</div>
                      </div>
                      <div className="flex-1 shrink self-stretch my-auto basis-0 text-ellipsis text-slate-600">
                        vs last month
                      </div>
                    </div>
                  </div>
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/d22d0e88a47e809d6028032b0a9da2df3a9b067d?placeholderIfAbsent=true"
                    className="object-contain shrink-0 w-24 aspect-[2]"
                  />
                </div>
                <div className="absolute top-4 right-4 z-0 w-5">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/174fe0ec676bf4780e267cf5e23f22bfc0464194?placeholderIfAbsent=true"
                    className="object-contain w-full aspect-square"
                    alt="Options icon"
                  />
                </div>
              </div>
              <div className="mt-6 w-full">
                <div className="w-full">
                  <div className="pl-3 w-full text-lg font-semibold leading-loose text-gray-900">
                    <div className="w-full">
                      <div className="flex gap-4 w-full">
                        <div className="flex-1 shrink w-full basis-0 min-w-60">
                          Diagnostic Checklist
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center p-5 mt-6 w-full bg-white rounded-xl border border-solid shadow-sm border-[#EAECF0] min-w-[216px]">
                    <div className="flex gap-6 items-center w-full">
                      <div className="flex flex-1 shrink gap-2 items-start self-stretch my-auto w-full basis-0 min-w-60">
                        <div className="flex justify-center items-center pt-0.5 w-4 h-4 relative">
                          <input 
                            type="checkbox"
                            id="check-taps"
                            checked={checkedItems.taps}
                            onChange={() => handleCheckboxChange("taps")}
                            className="absolute opacity-0 w-4 h-4 cursor-pointer z-10"
                            aria-label="Check all taps are fully closed"
                          />
                          <div
                            className={`flex self-stretch my-auto w-4 h-4 rounded border border-solid ${
                              checkedItems.taps
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300"
                            }`}
                          >
                            {checkedItems.taps && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 5L6.5 10.5L4 8"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <label htmlFor="check-taps" className="flex-1 shrink text-sm font-medium leading-none basis-0 min-w-60 text-slate-700 cursor-pointer">
                          Check all taps are fully closed
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center p-5 mt-6 w-full bg-white rounded-xl border border-solid shadow-sm border-[#EAECF0] min-w-[216px]">
                    <div className="flex gap-6 items-center w-full">
                      <div className="flex flex-1 shrink gap-2 items-start self-stretch my-auto w-full basis-0 min-w-60">
                        <div className="flex justify-center items-center pt-0.5 w-4 h-4 relative">
                          <input 
                            type="checkbox"
                            id="check-toilets"
                            checked={checkedItems.toilets}
                            onChange={() => handleCheckboxChange("toilets")}
                            className="absolute opacity-0 w-4 h-4 cursor-pointer z-10"
                            aria-label="Listen for running toilets"
                          />
                          <div
                            className={`flex self-stretch my-auto w-4 h-4 rounded border border-solid ${                              checkedItems.toilets
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300"
                            }`}
                          >
                            {checkedItems.toilets && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 5L6.5 10.5L4 8"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <label htmlFor="check-toilets" className="flex-1 shrink text-sm font-medium leading-none basis-0 min-w-60 text-slate-700 cursor-pointer">
                          Listen for running toilets
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col justify-center p-5 mt-6 w-full bg-white rounded-xl border border-solid shadow-sm border-[#EAECF0] min-w-[216px]">
                    <div className="flex gap-6 items-center w-full">
                      <div className="flex flex-1 shrink gap-2 items-start self-stretch my-auto w-full basis-0 min-w-60">
                        <div className="flex justify-center items-center pt-0.5 w-4 h-4 relative">
                          <input 
                            type="checkbox"
                            id="check-hoses"
                            checked={checkedItems.hoses}
                            onChange={() => handleCheckboxChange("hoses")}
                            className="absolute opacity-0 w-4 h-4 cursor-pointer z-10"
                            aria-label="Check outdoor hose connections"
                          />
                          <div
                            className={`flex self-stretch my-auto w-4 h-4 rounded border border-solid ${                              checkedItems.hoses
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300"
                            }`}
                          >
                            {checkedItems.hoses && (
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12 5L6.5 10.5L4 8"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                        </div>
                        <label htmlFor="check-hoses" className="flex-1 shrink text-sm font-medium leading-none basis-0 min-w-60 text-slate-700 cursor-pointer">
                          Check outdoor hose connections
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 w-full text-base font-semibold">
                    <button className="flex overflow-hidden gap-1.5 justify-center items-center px-4 py-2.5 w-full text-white bg-blue-600 rounded-lg border border-solid shadow-sm border-[#1570EF]">
                      <div className="self-stretch px-0.5 my-auto">
                        Yes, I Found a Leak
                      </div>
                    </button>
                    <button className="flex overflow-hidden gap-1.5 justify-center items-center px-4 py-2.5 mt-4 w-full bg-gray-200 rounded-lg border border-solid shadow-sm border-[#D0D5DD] text-slate-700">
                      <div className="self-stretch px-0.5 my-auto">
                        No Leak Found (False Alarm)
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex gap-6 items-start mt-6 w-full">
                <div className="flex relative flex-col flex-1 shrink items-start px-4 py-5 bg-white rounded-xl border border-solid shadow-sm basis-0 border-[#EAECF0]">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/522bdd018c277298f01ff97bbf9bfd1d93d5bc0c?placeholderIfAbsent=true"
                    className="object-contain z-0 w-12 rounded-full aspect-square"
                    alt="Leak history icon"
                  />
                  <div className="z-0 self-stretch mt-5 w-full text-sm font-medium leading-none text-slate-600">
                    View Leak History
                  </div>
                  <div className="absolute top-4 right-4 z-0 w-5">
                    <img
                      src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/174fe0ec676bf4780e267cf5e23f22bfc0464194?placeholderIfAbsent=true"
                      className="object-contain w-full aspect-square"
                      alt="Options icon"
                    />
                  </div>
                </div>
                <div className="flex relative flex-col flex-1 shrink items-start px-4 py-5 bg-white rounded-xl border border-solid shadow-sm basis-0 border-[#EAECF0]">
                  <img
                    src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/756ac855c22c250d8660dbd1664e65239c92fc9e?placeholderIfAbsent=true"
                    className="object-contain z-0 w-12 rounded-full aspect-square"
                    alt="Find plumber icon"
                  />
                  <div className="z-0 self-stretch mt-5 w-full text-sm font-medium leading-none text-slate-600">
                    Find a Plumber
                  </div>
                  <div className="absolute top-4 z-0 w-5 right-[15px]">
                    <img
                      src="https://cdn.builder.io/api/v1/image/assets/e8fd2a11e5e6412dbddb276a310a5dca/54b911beeef099a585d0fbe2bebf6499ab7eae8e?placeholderIfAbsent=true"
                      className="object-contain w-full aspect-square"
                      alt="Arrow icon"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TabBar />
    </main>
  );
};

export default LeakDetectionDashboard;
